import t from 'react-translate';
import { useContext } from 'react';
import { config } from '@config/pendo.config.json';
import prodPathReplace from 'shared/prod-path-rewrite';
import { SessionTypes } from 'redux/schemas/models/live-event';

import { Button } from 'react-bootstrap';
import ClickableContainer from 'components/clickable-container';
import MentoringProgramContext from 'athena/components/mentoring-program/context';
import NvDropdown, {
  NvDropdownButtonStyle,
  NvDropdownCustomItem,
} from 'shared/components/inputs/nv-dropdown';
import {
  ALL_INTEGRATION_TYPES,
  sessionTypeOptions,
  translationIntegrationKeys,
} from 'redux/schemas/models/mentoring-program-sessions';

type MakeVirtualMeetingItemProps = {
  id: SessionTypes;
  img: string;
  onClick: (type: SessionTypes) => void;
};

const MakeVirtualMeetingItem = (props: MakeVirtualMeetingItemProps) => {
  const { id, img, onClick } = props;

  const translations = t.MENTORING_PROGRAMS.PARTICIPANT_HOME.PLAN_SESSION_FLYOUT.VIRTUAL_MEETING.MAKE_VIRTUAL_MEETING.DROPDOWN;
  const translationKey = translationIntegrationKeys[id];
  const text = translations[translationKey]();

  const { mentoringProgram } = useContext(MentoringProgramContext);
  const dataQa = (
    config.pendo.athena.mentorshipProgram.participantHome.sessions.planSessionFlyout.virtualMeeting.makeVirtualMeeting
  );
  const dataQaId = `${dataQa}_${mentoringProgram?.id}`;

  return (
    <ClickableContainer
      className='m-4 d-flex align-items-center'
      onClick={() => onClick(id)}
      data-qa={dataQa}
      data-qa-id={dataQaId}
      aria-label={text}
    >
      <img
        src={prodPathReplace(img)}
        alt={text}
        className='mr-4'
        width={24}
        height={24}
      />
      {text}
    </ClickableContainer>
  );
};

type MakeVirtualMeetingButtonProps = {
  type: SessionTypes | typeof ALL_INTEGRATION_TYPES;
  onClick: (type: SessionTypes) => void;
};

const MakeVirtualMeetingButton = (props: MakeVirtualMeetingButtonProps) => {
  const { type, onClick } = props;

  const translations = t.MENTORING_PROGRAMS.PARTICIPANT_HOME.PLAN_SESSION_FLYOUT.VIRTUAL_MEETING.MAKE_VIRTUAL_MEETING;
  const translationKey = translationIntegrationKeys[type];
  const makeVirtualMeetingText = translations[translationKey]();

  const { mentoringProgram } = useContext(MentoringProgramContext);
  const dataQa = (
    config.pendo.athena.mentorshipProgram.participantHome.sessions.planSessionFlyout.virtualMeeting.makeVirtualMeetingDropdown
  );
  const dataQaId = `${dataQa}_${mentoringProgram?.id}`;

  const handleClick = () => {
    if (type !== ALL_INTEGRATION_TYPES) onClick(type);
  };

  const getButton = () => (
    <Button
      variant='secondary'
      className='semi-bold'
      onClick={handleClick}
      data-qa={dataQa}
      data-qa-id={dataQaId}
      aria-label={makeVirtualMeetingText}
    >
      {makeVirtualMeetingText}
    </Button>
  );

  if (type !== ALL_INTEGRATION_TYPES) return getButton();

  const options = Object.values(sessionTypeOptions);
  const items: NvDropdownCustomItem[] = options.map(({ id, img }) => ({
    id,
    type: 'custom',
    customItem: (
      <MakeVirtualMeetingItem
        id={id}
        img={img}
        onClick={onClick}
      />
    ),
  }));

  return (
    <NvDropdown
      items={items}
      buttonStyle={NvDropdownButtonStyle.CUSTOM}
      customTarget={getButton}
    />
  );
};

export default MakeVirtualMeetingButton;
